import { useMemo } from 'react';
import {
  AspectRatio,
  Flex,
  HStack,
  Text,
  Tooltip,
  VStack,
} from '@chakra-ui/react';
import { Img, Link } from 'components';

import { getCategoryIcon } from 'utils/helpers';

const imageSize = {
  height: 280,
  width: 185,
} as const;

interface Props {
  outlet: AdaptedStore | null;
}

export const FloatingOutlet = (props: Props) => {
  const { outlet } = props;

  const titleImageUrl = useMemo(() => {
    if (!outlet?.titleImage?.url) return '';

    const url = new URL(outlet.titleImage.url);

    url.searchParams.set('height', String(imageSize.height));
    url.searchParams.set('width', String(imageSize.width));
    url.searchParams.set('mode', 'fit');

    return url.href;
  }, [outlet?.titleImage?.url]);

  if (!outlet) return null;

  return (
    <HStack
      bg="white"
      bottom="2.25rem"
      maxW="24rem"
      rounded="lg"
      shadow="lg"
      as={Link}
      href={`/${outlet.zmyleUri}`}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      noHoverEffect
    >
      <AspectRatio ratio={3 / 2} w="13rem">
        <Img
          src={titleImageUrl}
          rounded="lg"
          shadow="lg"
          bg="gray.50"
          width={imageSize.width}
          height={imageSize.height}
          alt={outlet.name}
        />
      </AspectRatio>

      <VStack h="full" w="full" px="0.5rem" align="flex-start">
        <Text
          fontSize="1rem"
          fontWeight={500}
          overflow="hidden"
          textOverflow="ellipsis"
          whiteSpace="nowrap"
          w="full"
          alignSelf="flex-start"
          maxW="11rem"
          title={outlet.name}
        >
          {outlet.name}
        </Text>

        <VStack w="full" h="full" align="flex-start" justify="space-between">
          <HStack justify="space-between" align="flex-end" w="full">
            <Flex direction="column" align="flex-start" fontWeight={300}>
              <Text fontSize="0.875rem">{outlet.address.streetAddress}</Text>

              <Text fontSize="0.875rem">
                {outlet.address.zipcode} {outlet.address.city}
              </Text>
            </Flex>

            <Tooltip
              hasArrow
              label={outlet.category?.name}
              aria-label="outlet category"
            >
              <span>
                <Img
                  src={getCategoryIcon(outlet.parentCategoryId)}
                  alt="Store Category"
                />
              </span>
            </Tooltip>
          </HStack>
        </VStack>
      </VStack>
    </HStack>
  );
};
